import {
  BUTTON_COLORS,
  BUTTON_TYPE,
  CUSTOM_SIZES,
  DYNAMIC_POPUP_TYPES,
  ERROR_TYPE,
  GEOLOCATION_STEPS,
  POPUP_TYPES,
  SECONDARY_POPUP_TYPES,
} from './constants';

export { GEOLOCATION_STEPS };
import {
  EXIT_POPUP_STRUCTURE,
  EXIT_RECENTLY_PLAYED_POPUP_STRUCTURE,
  GAME_ERRORS,
  GEOLOCATION_REFRESH_POPUP_STRUCTURE,
  SWJ_POPUP_STRUCTURE,
} from './popup-structure';

export interface ErrorPopupHandlers {
  exitGame: (redirectUrl?: string) => void;
  dismissErrorMessage: () => void;
  chooseDialog: (arg: number) => void;
  handleDismiss?: () => void;
  buttonStyle?: string;
  buttonMessage?: string;
  disableJackpot?: boolean;
}

export type SecondaryPopupType =
  (typeof SECONDARY_POPUP_TYPES)[keyof typeof SECONDARY_POPUP_TYPES];

export type GameErrorType =
  | (typeof ERROR_TYPE)[keyof typeof ERROR_TYPE]
  | SecondaryPopupType;

export type PopupType = (typeof POPUP_TYPES)[keyof typeof POPUP_TYPES];

export type DynamicPopupType =
  (typeof DYNAMIC_POPUP_TYPES)[keyof typeof DYNAMIC_POPUP_TYPES];

export type AllPopupTypes = GameErrorType & PopupType & DynamicPopupType;

export type ButtonType = (typeof BUTTON_TYPE)[keyof typeof BUTTON_TYPE];

export type ButtonProps = {
  buttonClass?: string;
  buttonMessage: string;
  buttonType: ButtonType;
  onClick?: () => void;
  /** Stable automation hook rendered as `data-testid` on the button. */
  dataTestId?: string;
};

interface Error {
  props: {
    buttons?: ButtonProps[];
    popupMessage?: string;
    popupName: string;
  };
  secondary?: FooterOptions;
  errorType: GameErrorType | SecondaryPopupType;
}

export type CustomSizeType = (typeof CUSTOM_SIZES)[keyof typeof CUSTOM_SIZES];

export type ButtonColorType =
  (typeof BUTTON_COLORS)[keyof typeof BUTTON_COLORS];

export type PopupPropsType = {
  customSize?: CustomSizeType;
  closeButton?: boolean;
  horizontalButtons?: boolean;
  buttons?: ButtonProps[];
  popupMessage?: string;
  popupName?: string;
  /** Marks a popup whose visible props change per step (e.g. GEOLOCATION_REFRESH). */
  dynamicSteps?: boolean;
  /** Primary CTA wiring for dynamic/stepped popups. */
  mainButton?: ButtonType;
  mainButtonMessage?: string;
  mainButtonColor?: ButtonColorType;
  /** Extra side-effect fired when the main CTA is pressed (e.g. DD tracking). */
  mainButtonExtraAction?: () => void;
  autoClose?: {
    ms: number;
    targetedPopup: DynamicPopupType | PopupType;
  };
};

export type GeolocationStepsType =
  (typeof GEOLOCATION_STEPS)[keyof typeof GEOLOCATION_STEPS];

// for future steps
export type AllPopupStepsType = GeolocationStepsType;

export interface IPopup {
  props: PopupPropsType;
  dynamicProps?: {
    [key in GeolocationStepsType]?: PopupPropsType;
  };
  secondary?: FooterOptions;
  errorType?: string;
}

export type GameErrors = {
  [key in GameErrorType]: Error;
};

export type FooterOptions = {
  secondaryPopup: SecondaryPopupType;
  rgIcon?: boolean;
  includeErrorCode?: boolean; // default true
  messages: {
    firstMessage: string;
    actionMessage: string;
  };
  /** Stable automation hooks rendered as `data-testid` on the footer text/action. */
  testIds?: {
    text?: string;
    action?: string;
  };
};

export type PopupTypes = (typeof POPUP_TYPES)[keyof typeof POPUP_TYPES];

export type PopupButtonConfig = {
  buttonClass: string;
  buttonMessage: string;
  buttonType: string;
  onClick?: () => void;
};

export type PopupConfig = {
  header?: {
    backButton?: React.ReactNode;
    closeButton?: React.ReactNode;
  };
  main: {
    icon?: boolean | React.ReactNode;
    popupName?: string;
    popupMessage?: string;
    buttons: React.ReactNode[];
    customSize?: CustomSizeType;
    horizontalButtons?: boolean;
  };
  footer?: null | React.ReactNode;
  errorData?: PopupErrorData;
};

export type PopupProps = {
  config: PopupConfig;
  onClose?: () => void;
  type?: PopupTypes;
  errorData?: PopupErrorData;
  customConfig?: PopupConfig;
};

export type PopupErrorData = {
  errorCode?: string;
  errorType?: GameErrorType;
  errorParameters?: string[];
};

export type PopupContextType = {
  showPopup: (
    data: PopupConfig,
    type?: PopupTypes,
    errorData?: PopupErrorData,
  ) => void;
  hidePopup: () => void;
};

export const getErrorType = (type: string): Error => {
  return GAME_ERRORS[type]
    ? GAME_ERRORS[type]
    : GAME_ERRORS[ERROR_TYPE.GENERIC];
};

export const getPopupType = (
  type: string,
  errorType: GameErrorType = ERROR_TYPE.GENERIC,
): IPopup => {
  switch (type) {
    case POPUP_TYPES.EXIT:
      return EXIT_POPUP_STRUCTURE;
    case POPUP_TYPES.EXIT_RECENTLY:
      return EXIT_RECENTLY_PLAYED_POPUP_STRUCTURE;
    case POPUP_TYPES.PROVIDER:
      return getErrorType(errorType);
    case POPUP_TYPES.SWJ:
      return SWJ_POPUP_STRUCTURE;
    case POPUP_TYPES.JACKPOT_WIN:
    case POPUP_TYPES.IN_GAME_MERCH:
      return { props: {} };
    case DYNAMIC_POPUP_TYPES.GEOLOCATION_REFRESH:
      return GEOLOCATION_REFRESH_POPUP_STRUCTURE;
    default:
      return getErrorType(type);
  }
};
